import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { altDealStructureOptionsViewed } from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { buildDealStructurePredictionsFromSlots } from './altDealStructureCdlUtils';
import { useGridData } from './contexts';

/**
 * Emits the analytics "viewed" event for the AI alternative deal structure options — a headless
 * (render-null) tracker mounted once alongside the finance/lease grids. It records that the shopper was
 * presented with the recommended structures, capturing the full set of options in a single event.
 *
 * Fires on the pending→settled edge (every slot non-pending) so the payload is complete: once on the
 * initial load, and again after a retry re-populates rows that had failed — so the event reflects the
 * now-complete grid rather than the partial first pass. A retry is detected by the option set being
 * unchanged (same slot ids); a term/down-payment *edit* changes the option set and is intentionally NOT
 * re-emitted here — that interaction is owned by dc-mv-292. The CDL tag is dc-mv-289, and the middleware
 * routes each deal type to its own path — finance (AI) → deal.predictions.dealStructure, lease (non-AI) →
 * deal.dealStructure.
 */
export const AltDealStructureOptionsViewTracker: FC = () => {
    const dispatch = useDispatch();
    const vehicleInfo = useSelector(vehicleSelectors.getVehicleAnalyticsPayload);
    const { slots } = useGridData();
    const wasSettledRef = useRef(false);
    const firedOptionSetRef = useRef<string | null>(null);

    const allSettled = slots.length > 0 && slots.every((slot) => slot.status !== 'pending');
    // Signature of the presented options; stable across a retry (same cells reload), changes on an edit.
    const optionSetKey = slots
        .map((slot) => slot.id)
        .sort()
        .join('|');

    useEffect(() => {
        const justSettled = allSettled && !wasSettledRef.current;
        wasSettledRef.current = allSettled;
        if (!justSettled) return;
        // Fire on the first settle and on a retry of the same options; skip a settle for a changed option
        // set (an edit — dc-mv-292 covers it). Either way, rebaseline so a later retry of these options fires.
        const shouldFire = firedOptionSetRef.current === null || firedOptionSetRef.current === optionSetKey;
        firedOptionSetRef.current = optionSetKey;
        if (!shouldFire) return;
        dispatch(altDealStructureOptionsViewed(vehicleInfo, buildDealStructurePredictionsFromSlots(slots)));
    }, [allSettled, optionSetKey, slots, dispatch, vehicleInfo]);

    return null;
};
